// src/components/Home/HomeBeneficios.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Row,
  Col,
  Button,
  Form,
  Modal,
  Placeholder, // 👈 para skeleton
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import api from "../../api/axios";
import BenefitCard from "./BenefitCard";
import GlobalRankingsHome from "./GlobalRankingsHome";
import { useAppToast } from "../Common/Toast/AppToastProvider";
import LeafletMap from "../Common/Map/LeafletMap";

// Iconos locales de categorías
import catGastronomia from "../../assets/categorias/gastronomia.png";
import catBelleza from "../../assets/categorias/belleza.png";
import catIndumentaria from "../../assets/categorias/indumentaria.png";
import catTecnologia from "../../assets/categorias/entretenimiento.png";
import catHogar from "../../assets/categorias/hogar.png";
import catMascotas from "../../assets/categorias/mascotas.png";
import catServicios from "../../assets/categorias/servicios.png";

// Mapa rubro_id → ícono + nombre
const CATEGORY_ICONS = {
  1: { img: catGastronomia, label: "Gastronomía" },
  2: { img: catBelleza, label: "Belleza" },
  3: { img: catIndumentaria, label: "Retail" },
  4: { img: catServicios, label: "Servicios" },
  5: { img: catTecnologia, label: "Tecnología" },
  6: { img: catMascotas, label: "Mascotas" },
  7: { img: catHogar, label: "Hogar" },
};



// === Helpers de estado (igual que en Catálogo) ===
const isActive = (b) => {
  const statusOk =
    b.status === 1 ||
    b.status === "active" ||
    typeof b.status === "undefined";

  const now = new Date();
  const validUntil = b.valid_until ? new Date(b.valid_until) : null;

  const expiredByFlag =
    typeof b.is_expired !== "undefined" ? Boolean(b.is_expired) : false;
  const expiredByDate = validUntil ? validUntil < now : false;

  return statusOk && !(expiredByFlag || expiredByDate);
};

const hexToRgba = (hex, alpha = 0.04) => {
  if (!hex) return `rgba(0,0,0,${alpha})`;
  let c = hex.replace("#", "");
  if (c.length === 3) {
    c = c
      .split("")
      .map((ch) => ch + ch)
      .join("");
  }
  const num = parseInt(c, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const parseCoord = (value) => {
  if (value === null || value === undefined) return NaN;
  const normalized = String(value).trim().replace(",", ".");
  const num = Number(normalized);
  return Number.isFinite(num) ? num : NaN;
};

const isValidLatLng = (lat, lng) =>
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  lat >= -90 &&
  lat <= 90 &&
  lng >= -180 &&
  lng <= 180;

const normalizeCityKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const toCityLabel = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (m) => m.toUpperCase());

const INITIAL_BENEFITS_VISIBLE = 12;
const BENEFITS_LOAD_STEP = 8;

const HomeBeneficios = () => {
  const { showToast } = useAppToast();
  const [comercios, setComercios] = useState([]);
  const [rubros, setRubros] = useState([]);
  const [mapItems, setMapItems] = useState([]);
  const [loadingMapItems, setLoadingMapItems] = useState(false);
  const [mapError, setMapError] = useState("");
  const [mapModuleEnabled, setMapModuleEnabled] = useState(true);
  const [selectedMapBusinessId, setSelectedMapBusinessId] = useState(null);
  const [selectedCity, setSelectedCity] = useState(
    () => localStorage.getItem("home_selected_city") || "all"
  );
  const [showCityPrompt, setShowCityPrompt] = useState(false);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [debouncedMapQuery, setDebouncedMapQuery] = useState("");
  const [selectedRubro, setSelectedRubro] = useState("all");
  const [hoveredRubro, setHoveredRubro] = useState(null); // 👈 para hover
  const [mobileCategoryAnimDir, setMobileCategoryAnimDir] = useState("right");
  const [visibleBenefitsCount, setVisibleBenefitsCount] = useState(INITIAL_BENEFITS_VISIBLE);

  const navigate = useNavigate();
  const benefitsListRef = useRef(null);
  const mapRequestSeqRef = useRef(0);
  const lastNonEmptyMapItemsRef = useRef([]);
  const token = localStorage.getItem("token");

  const trackInterestEvent = async (payload) => {
    if (!token) return;
    try {
      await api.post("/recommendations/events", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      // Tracking best-effort: no bloquear UI
      console.warn("No se pudo registrar evento de interes:", err?.message || err);
    }
  };

  // ==================== FETCH HOME + RUBROS ====================
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [homeRes, rubrosRes] = await Promise.allSettled([
          api.get("/home", {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }),
          api.get("/rubros"),
        ]);

        // Comercios
        if (homeRes.status === "fulfilled") {
          setComercios(homeRes.value.data || []);
        } else {
          console.error("Error en /home:", homeRes.reason);
        }

        // Rubros desde API (si existe)
        if (rubrosRes.status === "fulfilled") {
          const data = rubrosRes.value.data;
          const rubrosData = Array.isArray(data)
            ? data
            : data?.rubros || data?.items || [];

          setRubros(rubrosData || []);
        } else {
          console.warn("No se pudo cargar /rubros:", rubrosRes.reason);
          setRubros([]);
        }
      } catch (err) {
        console.error("Error al traer datos del home:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    if (selectedRubro === "all") return;

    trackInterestEvent({
      event_type: "filter_category_selected",
      rubro_id: Number(selectedRubro),
      metadata: { source: "home" },
    });
  }, [selectedRubro, token]);

  useEffect(() => {
    if (!token) return;
    const q = query.trim();
    if (q.length < 3) return;

    const t = setTimeout(() => {
      trackInterestEvent({
        event_type: "search_query",
        search_query: q,
        rubro_id: selectedRubro === "all" ? null : Number(selectedRubro),
        metadata: { source: "home" },
      });
    }, 700);

    return () => clearTimeout(t);
  }, [query, selectedRubro, token]);

  useEffect(() => {
    const loadFeatureFlags = async () => {
      try {
        const res = await api.get("/feature-flags/public");
        const enabled = Boolean(res.data?.flags?.map_module_enabled);
        setMapModuleEnabled(enabled);
      } catch (err) {
        setMapModuleEnabled(true);
      }
    };

    loadFeatureFlags();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedMapQuery(query), 350);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!mapModuleEnabled) {
      setLoadingMapItems(false);
      setMapItems([]);
      setMapError("");
      return;
    }

    const requestSeq = ++mapRequestSeqRef.current;

    const fetchMapBusinesses = async () => {
      try {
        if (requestSeq !== mapRequestSeqRef.current) return;
        setLoadingMapItems(true);
        setMapError("");

        const params = {};
        const trimmed = debouncedMapQuery.trim();
        if (trimmed) params.q = trimmed;
        if (selectedRubro !== "all") params.rubro_id = Number(selectedRubro);
        const res = await api.get("/business/public-locations", { params });
        if (requestSeq !== mapRequestSeqRef.current) return;
        const items = Array.isArray(res.data?.items) ? res.data.items : [];
        setMapItems(items);
        if (!items.length) {
          setMapError("No hay locales para la categoría/filtro seleccionado.");
        }
      } catch (err) {
        console.error("Error cargando mapa de comercios:", err);
        if (requestSeq !== mapRequestSeqRef.current) return;
        setMapItems([]);
        setMapError("No pudimos cargar comercios para el mapa.");
      } finally {
        if (requestSeq !== mapRequestSeqRef.current) return;
        setLoadingMapItems(false);
      }
    };

    fetchMapBusinesses();
  }, [debouncedMapQuery, selectedRubro, mapModuleEnabled]);

  const handleClaim = async (benefitId) => {
    if (!token) {
      showToast("Inicia sesión para reclamar este beneficio.", "info", "Acción requerida");
      navigate(`/login?benefit=${benefitId}`);
      return;
    }

    try {
      const res = await api.post(
        `/benefits/claim/${benefitId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showToast(`Beneficio reclamado. Código: ${res.data.claimCode}`, "success", "Listo");

      setComercios((prev) =>
        prev.map((c) => ({
          ...c,
          benefits: (c.benefits || []).map((b) =>
            b.id === benefitId ? { ...b, claimed: true } : b
          ),
        }))
      );
    } catch (err) {
      console.error("Error al reclamar beneficio:", err);
      showToast(
        err?.response?.data?.message || "No se pudo reclamar el beneficio.",
        "danger",
        "Error"
      );
    }
  };

  // ==================== FILTROS ====================

  const comerciosSoloActivos = useMemo(() => {
    return (comercios || [])
      .map((c) => ({
        ...c,
        benefits: (c.benefits || []).filter(isActive),
      }))
      .filter((c) => (c.benefits || []).length > 0);
  }, [comercios]);

  const comerciosPorRubro = useMemo(() => {
    if (selectedRubro === "all") return comerciosSoloActivos;

    const selId = Number(selectedRubro);
    return comerciosSoloActivos.filter(
      (c) => Number(c.rubro_id) === selId
    );
  }, [comerciosSoloActivos, selectedRubro]);

  const beneficiosFiltrados = useMemo(() => {
    const q = query.trim().toLowerCase();

    if (!q) return comerciosPorRubro;

    return comerciosPorRubro
      .map((c) => ({
        ...c,
        benefits: (c.benefits || []).filter(
          (b) =>
            b.name?.toLowerCase().includes(q) ||
            b.description?.toLowerCase().includes(q) ||
            c.name?.toLowerCase().includes(q)
        ),
      }))
      .filter((c) => c.benefits.length > 0);
  }, [comerciosPorRubro, query]);
  const allBenefitCards = useMemo(
    () =>
      beneficiosFiltrados.flatMap((comercio) =>
        (comercio.benefits || []).map((benefit) => ({
          comercio,
          benefit,
        }))
      ),
    [beneficiosFiltrados]
  );
  const visibleBenefitCards = useMemo(
    () => allBenefitCards.slice(0, visibleBenefitsCount),
    [allBenefitCards, visibleBenefitsCount]
  );
  const canLoadMoreBenefits = visibleBenefitsCount < allBenefitCards.length;

  useEffect(() => {
    setVisibleBenefitsCount(INITIAL_BENEFITS_VISIBLE);
  }, [query, selectedRubro, comercios]);





  // ==================== RENDER ====================

  const selectedRubroData =
    selectedRubro === "all"
      ? null
      : rubros.find((r) => Number(r.id) === Number(selectedRubro)) || null;
  const mapSectionTitle = selectedRubroData
    ? `Comercios en mapa: ${selectedRubroData.name || "Categoria"}`
    : "Comercios en mapa: Todas las categorias";

  const contentBg =
    selectedRubroData && selectedRubroData.color
      ? hexToRgba(selectedRubroData.color, 0.2)
      : "transparent";

  // Opcional: ordenar rubros por id para mantener orden estable
  const orderedRubros = useMemo(
    () =>
      (rubros || [])
        .filter((r) => CATEGORY_ICONS[r.id])
        .sort((a, b) => Number(a.id) - Number(b.id)),
    [rubros]
  );
  const mobileCategoryOptions = useMemo(
    () => [
      { id: "all", label: "Todos", icon: null, color: "#111827" },
      ...orderedRubros.map((r) => ({
        id: String(r.id),
        label: CATEGORY_ICONS[r.id]?.label || r.name || "Categoria",
        icon: CATEGORY_ICONS[r.id]?.img || null,
        color: r.color || "#2563EB",
      })),
    ],
    [orderedRubros]
  );
  const mobileActiveIndex = useMemo(() => {
    const idx = mobileCategoryOptions.findIndex((item) => item.id === String(selectedRubro));
    return idx >= 0 ? idx : 0;
  }, [mobileCategoryOptions, selectedRubro]);

  const validMapItems = useMemo(
    () =>
      mapItems.filter(
        (item) =>
          isValidLatLng(parseCoord(item.latitude), parseCoord(item.longitude))
      ),
    [mapItems]
  );

  const availableCities = useMemo(() => {
    const map = new Map();
    validMapItems.forEach((item) => {
      const rawCity = String(item.city || "").trim();
      if (!rawCity) return;
      const key = normalizeCityKey(rawCity);
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, toCityLabel(rawCity));
      }
    });
    return Array.from(map.entries())
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "es"));
  }, [validMapItems]);

  useEffect(() => {
    localStorage.setItem("home_selected_city", selectedCity);
  }, [selectedCity]);

  const mapItemsByRubro = useMemo(() => {
    if (selectedRubro === "all") return validMapItems;
    const rubroNum = Number(selectedRubro);
    return validMapItems.filter((item) => Number(item.rubro_id) === rubroNum);
  }, [validMapItems, selectedRubro]);

  const cityFilteredMapItems = useMemo(() => {
    if (selectedCity === "all") return mapItemsByRubro;
    return mapItemsByRubro.filter(
      (item) => normalizeCityKey(item.city) === selectedCity
    );
  }, [mapItemsByRubro, selectedCity]);

  const displayMapItems = cityFilteredMapItems;

  useEffect(() => {
    if (displayMapItems.length > 0) {
      lastNonEmptyMapItemsRef.current = displayMapItems;
    }
  }, [displayMapItems]);

  const mapItemsForRender = useMemo(() => {
    if (displayMapItems.length > 0) return displayMapItems;
    if (loadingMapItems && lastNonEmptyMapItemsRef.current.length > 0) {
      return lastNonEmptyMapItemsRef.current;
    }
    return displayMapItems;
  }, [displayMapItems, loadingMapItems]);
  const isMapInitialLoading = loadingMapItems && mapItemsForRender.length === 0;

  const cityCenter = useMemo(() => {
    if (!displayMapItems.length) return null;
    const acc = displayMapItems.reduce(
      (sum, item) => ({
        lat: sum.lat + parseCoord(item.latitude),
        lng: sum.lng + parseCoord(item.longitude),
      }),
      { lat: 0, lng: 0 }
    );
    return {
      lat: acc.lat / displayMapItems.length,
      lng: acc.lng / displayMapItems.length,
    };
  }, [displayMapItems]);

  const mapCenterForRender = useMemo(() => {
    if (!mapItemsForRender.length) return null;
    const acc = mapItemsForRender.reduce(
      (sum, item) => ({
        lat: sum.lat + parseCoord(item.latitude),
        lng: sum.lng + parseCoord(item.longitude),
      }),
      { lat: 0, lng: 0 }
    );
    return {
      lat: acc.lat / mapItemsForRender.length,
      lng: acc.lng / mapItemsForRender.length,
    };
  }, [mapItemsForRender]);

  const getDistanceLabelForBusinessId = () => null;
  const visibleMapCount = displayMapItems.length;
  const totalMapCount = validMapItems.length;
  const isSelectedCityInOptions =
    selectedCity === "all" || availableCities.some((city) => city.key === selectedCity);
  const selectedCityLabel =
    selectedCity === "all"
      ? "Todas las ciudades"
      : availableCities.find((city) => city.key === selectedCity)?.label || toCityLabel(selectedCity);

  useEffect(() => {
    if (!availableCities.length) return;
    const seen = localStorage.getItem("home_city_prompt_seen");
    if (seen === "1") return;
    setShowCityPrompt(true);
    localStorage.setItem("home_city_prompt_seen", "1");
  }, [availableCities]);

  const scrollToBenefitsList = () => {
    if (!benefitsListRef.current) return;
    benefitsListRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const goToPrevCategory = () => {
    const len = mobileCategoryOptions.length;
    if (!len) return;
    const nextIdx = (mobileActiveIndex - 1 + len) % len;
    setMobileCategoryAnimDir("left");
    setSelectedRubro(mobileCategoryOptions[nextIdx].id);
  };
  const goToNextCategory = () => {
    const len = mobileCategoryOptions.length;
    if (!len) return;
    const nextIdx = (mobileActiveIndex + 1) % len;
    setMobileCategoryAnimDir("right");
    setSelectedRubro(mobileCategoryOptions[nextIdx].id);
  };

  useEffect(() => {
    setSelectedMapBusinessId((prev) => {
      if (prev && displayMapItems.some((item) => item.id === prev)) return prev;
      return displayMapItems[0]?.id || null;
    });
  }, [displayMapItems]);

  // ==================== SKELETON MIENTRAS CARGA ====================
  if (loading) {
    return (
      <>
        {/* Navbar mejorado */}


        <div className="container mt-4">
          <h2 className="mb-4 text-center">Beneficios disponibles</h2>

          {/* Skeleton categorías */}
          <div className="mb-4">
            <div className="d-flex flex-wrap justify-content-center gap-4">
              {Array.from({ length: 7 }).map((_, idx) => (
                <div key={idx} style={{ textAlign: "center" }}>
                  <div
                    className="rounded-circle bg-light placeholder-glow"
                    style={{
                      width: 70,
                      height: 70,
                      marginBottom: 6,
                    }}
                  >
                    <Placeholder xs={12} style={{ opacity: 0 }} />
                  </div>
                  <Placeholder
                    xs={8}
                    style={{ height: 12 }}
                    className="bg-light"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Skeleton cards */}
          <Row className="g-3">
            {Array.from({ length: 8 }).map((_, idx) => (
              <Col key={idx} xs={12} sm={6} md={4} lg={3}>
                <div className="card shadow-sm">
                  <div
                    className="card-img-top bg-light placeholder-glow"
                    style={{ height: 140 }}
                  >
                    <Placeholder xs={12} style={{ opacity: 0 }} />
                  </div>
                  <div className="card-body">
                    <Placeholder as="h5" animation="glow">
                      <Placeholder xs={7} />
                    </Placeholder>
                    <Placeholder as="p" animation="glow">
                      <Placeholder xs={10} /> <Placeholder xs={8} />{" "}
                      <Placeholder xs={6} />
                    </Placeholder>
                    <Placeholder
                      xs={6}
                      style={{ height: 30 }}
                      className="rounded"
                    />
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </>
    );
  }

  // ==================== UI REAL ====================
  return (
    <>
      <style>{`
        @keyframes mapExploreBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(6px); }
        }
        @keyframes placeholderShimmer {
          0% { background-position: 100% 0; }
          100% { background-position: 0 0; }
        }
        .home-categories-sticky {
          position: sticky;
          top: calc(var(--main-navbar-height, 56px) + 8px);
          z-index: 1020;
          background: rgba(255, 255, 255, 0.97);
          backdrop-filter: blur(4px);
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 12px 10px 6px;
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.07);
        }
        .home-map-shell {
          border: 1px solid #dbe3ef;
          border-radius: 12px;
          overflow: hidden;
          margin-bottom: 12px;
          position: relative;
          box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
          background: #fff;
        }
        .home-map-legend {
          color: #64748b;
          font-weight: 500;
          margin-bottom: 10px;
        }
        .map-business-chip {
          border-radius: 999px !important;
          font-weight: 500 !important;
        }
        .map-business-chip.is-active {
          background: #0d6efd !important;
          color: #fff !important;
          border-color: #0d6efd !important;
        }
        .mobile-category-wrap {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 4px 6px 10px;
        }
        .mobile-category-arrow {
          width: 38px;
          height: 38px;
          border-radius: 999px;
          border: 1px solid #d1d5db;
          background: #fff;
          color: #111827;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          line-height: 1;
        }
        .mobile-category-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 90px;
          cursor: pointer;
          user-select: none;
        }
        .mobile-category-main--left {
          animation: mobileCategorySlideInLeft 220ms ease;
        }
        .mobile-category-main--right {
          animation: mobileCategorySlideInRight 220ms ease;
        }
        @keyframes mobileCategorySlideInLeft {
          from { opacity: 0; transform: translateX(-18px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes mobileCategorySlideInRight {
          from { opacity: 0; transform: translateX(18px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @media (max-width: 991.98px) {
          .home-categories-sticky {
            top: calc(var(--main-navbar-height, 64px) + 6px);
            padding: 10px 6px 2px;
          }
        }
      `}</style>
      <Modal show={showCityPrompt} onHide={() => setShowCityPrompt(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Seleccionar ciudad</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Elegi una ciudad para filtrar el mapa y los comercios disponibles.
          <Form.Select
            className="mt-3"
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
          >
            <option value="all">Todas las ciudades</option>
            {!isSelectedCityInOptions && selectedCity !== "all" && (
              <option value={selectedCity}>{toCityLabel(selectedCity)}</option>
            )}
            {availableCities.map((city) => (
              <option key={`city-modal-${city.key}`} value={city.key}>
                {city.label}
              </option>
            ))}
          </Form.Select>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowCityPrompt(false)}>
            Cerrar
          </Button>
          <Button variant="primary" onClick={() => setShowCityPrompt(false)}>
            Aplicar
          </Button>
        </Modal.Footer>
      </Modal>
{/* Navbar */}
{/* Navbar mejorado */}


      <div className="container mt-4">
        <h2 className="mb-4 text-center fw-semibold">Beneficios disponibles</h2>
        {/* BUSCADOR */}
        <Row className="gy-2 gx-2 align-items-end mb-3">
          <Col xs={12} md={{ span: 6, offset: 3 }}>
            <Form.Label>Buscar beneficios</Form.Label>
            <Form.Control
              placeholder="Ej: café, pizza, estética, tienda..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </Col>
        </Row>

        {/* CATEGORÍAS CON ICONOS + COLOR EN SELECCIÓN / HOVER */}
        <div className="mb-4 home-categories-sticky">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2 px-1">
            <div className="small text-muted fw-semibold">Filtrar por categoría</div>
            <div className="d-none d-md-flex align-items-center gap-2">
              <Form.Select
                size="sm"
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                style={{ minWidth: 190 }}
              >
                <option value="all">Todas las ciudades</option>
                {!isSelectedCityInOptions && selectedCity !== "all" && (
                  <option value={selectedCity}>{toCityLabel(selectedCity)}</option>
                )}
                {availableCities.map((city) => (
                  <option key={`city-categories-${city.key}`} value={city.key}>
                    {city.label}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="d-md-none">
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => setShowCityPrompt(true)}
              >
                Ciudad: {selectedCityLabel}
              </Button>
            </div>
          </div>
          <div className="d-md-none">
            {mobileCategoryOptions.length > 0 && (
              <div className="mobile-category-wrap">
                <button
                  type="button"
                  className="mobile-category-arrow"
                  onClick={goToPrevCategory}
                  aria-label="Categoria anterior"
                >
                  {"<"}
                </button>
                <div
                  key={`mobile-cat-${mobileCategoryOptions[mobileActiveIndex].id}-${mobileActiveIndex}`}
                  className={`mobile-category-main mobile-category-main--${mobileCategoryAnimDir}`}
                  onClick={() => setSelectedRubro(mobileCategoryOptions[mobileActiveIndex].id)}
                >
                  {mobileCategoryOptions[mobileActiveIndex].icon ? (
                    <div
                      style={{
                        width: 62,
                        height: 62,
                        borderRadius: "50%",
                        background: "#ffffff",
                        border: `2px solid ${mobileCategoryOptions[mobileActiveIndex].color}`,
                        boxShadow: "0 0 10px rgba(0,0,0,0.18)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: 6,
                      }}
                    >
                      <img
                        src={mobileCategoryOptions[mobileActiveIndex].icon}
                        alt={mobileCategoryOptions[mobileActiveIndex].label}
                        style={{ width: 36, height: 36 }}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        width: 62,
                        height: 62,
                        borderRadius: "50%",
                        background: "#111827",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 24,
                        marginBottom: 6,
                      }}
                    >
                      🏷️
                    </div>
                  )}
                  <div style={{ fontSize: ".95rem", fontWeight: 600 }}>
                    {mobileCategoryOptions[mobileActiveIndex].label}
                  </div>
                </div>
                <button
                  type="button"
                  className="mobile-category-arrow"
                  onClick={goToNextCategory}
                  aria-label="Categoria siguiente"
                >
                  {">"}
                </button>
              </div>
            )}
          </div>
          <div
            className="d-none d-md-flex flex-wrap justify-content-center gap-4"
            style={{ paddingBottom: 10 }}
          >
            {/* Categoría "Todos" */}
            <div
              onClick={() => setSelectedRubro("all")}
              onMouseEnter={() => setHoveredRubro("all")}
              onMouseLeave={() => setHoveredRubro(null)}
              style={{
                cursor: "pointer",
                textAlign: "center",
                opacity:
                  selectedRubro === "all" || hoveredRubro === "all" ? 1 : 0.5,
                transition: "all 0.2s ease",
                transform:
                  selectedRubro === "all" || hoveredRubro === "all"
                    ? "translateY(-2px) scale(1.03)"
                    : "none",
              }}
            >
              <div
                style={{
                  width: 70,
                  height: 70,
                  borderRadius: "50%",
                  background:
                    selectedRubro === "all" || hoveredRubro === "all"
                      ? "#111827"
                      : "#eee",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 26,
                  marginBottom: 6,
                  boxShadow:
                    selectedRubro === "all" || hoveredRubro === "all"
                      ? "0 0 10px rgba(0,0,0,0.35)"
                      : "0 0 4px rgba(0,0,0,0.1)",
                }}
              >
                🏷️
              </div>
              <div style={{ fontSize: ".9rem" }}>Todos</div>
            </div>

            {/* Categorías reales */}
            {orderedRubros.map((r) => {
              const info = CATEGORY_ICONS[r.id];
              if (!info) return null;

              const selected = Number(selectedRubro) === Number(r.id);
              const hovered = hoveredRubro === r.id;
              const activeCircle = selected || hovered;

              const baseColor = r.color || "#2563EB"; // fallback azul si no hay color
              const bgCircle = activeCircle ? baseColor : "#ffffff";
              const textColor = activeCircle ? baseColor : "#111827";
              const borderColor = baseColor;

              return (
                <div
                  key={r.id}
                  onClick={() => setSelectedRubro(r.id)}
                  onMouseEnter={() => setHoveredRubro(r.id)}
                  onMouseLeave={() => setHoveredRubro(null)}
                  style={{
                    cursor: "pointer",
                    textAlign: "center",
                    opacity: activeCircle ? 1 : 0.6,
                    transition: "all 0.2s ease",
                    transform: activeCircle
                      ? "translateY(-2px) scale(1.05)"
                      : "none",
                  }}
                >
                  <div
                    style={{
                      width: 70,
                      height: 70,
                      borderRadius: "50%",
                      background: bgCircle,
                      border: `2px solid ${borderColor}`,
                      boxShadow: activeCircle
                        ? "0 0 12px rgba(0,0,0,0.25)"
                        : "0 0 6px rgba(0,0,0,0.12)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 6,
                    }}
                  >
                    <img
                      src={info.img}
                      alt={info.label}
                      style={{
                        width: 40,
                        height: 40,
                        filter: activeCircle ? "brightness(1.05)" : "none",
                      }}
                    />
                  </div>
                  <div style={{ fontSize: ".9rem", color: textColor }}>
                    {info.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {mapModuleEnabled && (
        <div className="mb-4">
          <h5 className="mb-1 text-center fw-semibold">{mapSectionTitle}</h5>
          <div className="small home-map-legend text-center">
            ● {visibleMapCount} comercios visibles en el mapa
            {selectedRubro !== "all" || selectedCity !== "all"
              ? ` (de ${totalMapCount} con coordenadas)`
              : ""}
          </div>
          {mapError && <p className="text-danger small mb-2">{mapError}</p>}
          {isMapInitialLoading ? (
            <>
              <div
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: 10,
                  overflow: "hidden",
                  marginBottom: 10,
                  height: 320,
                  background: "linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 37%, #f3f4f6 63%)",
                  backgroundSize: "400% 100%",
                  animation: "placeholderShimmer 1.4s ease infinite",
                }}
              />
              <div className="d-flex flex-wrap gap-2">
                {Array.from({ length: 6 }).map((_, idx) => (
                  <span
                    key={`map-chip-skeleton-${idx}`}
                    style={{
                      width: 110,
                      height: 31,
                      borderRadius: 8,
                      background: "#e5e7eb",
                      display: "inline-block",
                    }}
                  />
                ))}
              </div>
            </>
          ) : mapItemsForRender.length > 0 ? (
            <>
              <div className="home-map-shell">
                <LeafletMap
                  items={mapItemsForRender}
                  selectedId={selectedMapBusinessId}
                  onSelect={setSelectedMapBusinessId}
                  height={320}
                  preferredCenter={mapCenterForRender || cityCenter}
                />
                {loadingMapItems && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(255,255,255,0.5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      color: "#374151",
                      pointerEvents: "none",
                    }}
                  >
                    Actualizando mapa...
                  </div>
                )}
              </div>
              <div className="d-flex flex-wrap gap-2">
                {displayMapItems.slice(0, 12).map((item) => {
                  const selected = item.id === selectedMapBusinessId;
                  return (
                    <Button
                      key={`map-item-${item.id}`}
                      size="sm"
                      variant="outline-primary"
                      className={`map-business-chip ${selected ? "is-active" : ""}`}
                      onClick={() => setSelectedMapBusinessId(item.id)}
                    >
                      {item.name}
                    </Button>
                  );
                })}
              </div>
              <div className="text-center mt-3">
                <button
                  type="button"
                  onClick={scrollToBenefitsList}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#0d6efd",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Explora los beneficios debajo
                  <span
                    style={{
                      display: "inline-block",
                      marginLeft: 8,
                      animation: "mapExploreBounce 1.1s ease-in-out infinite",
                    }}
                  >
                    v
                  </span>
                </button>
              </div>
            </>
          ) : validMapItems.length > 0 ? (
            <p className="text-muted mb-0">
              No hay comercios para esa ciudad con la categoria/filtro actual.
            </p>
          ) : mapItems.length > 0 ? (
            <p className="text-muted mb-0">
              Hay comercios en el resultado, pero algunos no tienen coordenadas validas.
            </p>
          ) : (
            <p className="text-muted mb-0">No hay comercios con coordenadas para estos filtros.</p>
          )}
        </div>
        )}

        {/* LISTADO DE BENEFICIOS + fondo dinámico por rubro */}
        <div
          id="beneficios-listado"
          ref={benefitsListRef}
          style={{
            backgroundColor: contentBg,
            borderRadius: "12px",
            padding: "12px",
            transition: "background-color 0.25s ease-in-out",
          }}
        >
          {allBenefitCards.length === 0 ? (
            selectedRubro !== "all" ? (
              <div className="text-center py-5">
                <h5 className="mb-2">
                  Todavía no hay beneficios en este rubro.
                </h5>
                <p className="text-muted mb-4">
                  Si tenés un local de este tipo, podés sumarte a QR Benefits.
                </p>
                <Button
                  variant="primary"
                  onClick={() =>
                    navigate(`/contacto-comercios?rubro=${selectedRubro}`)
                  }
                >
                  Quiero sumar mi local
                </Button>
              </div>
            ) : (
              <p className="text-center text-muted py-5">
                No hay beneficios activos con estos filtros.
              </p>
            )
          ) : (
            <>
              <Row>
                {visibleBenefitCards.map(({ comercio, benefit }) => (
                  <Col
                    key={`${comercio.id}-${benefit.id}`}
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    className="mb-3"
                  >
                    <BenefitCard
                      benefit={benefit}
                      comercio={{ name: comercio.name, slug: comercio.slug }}
                      distanceLabel={getDistanceLabelForBusinessId(comercio.id)}
                      onClaim={handleClaim}
                    />
                  </Col>
                ))}
              </Row>
              {canLoadMoreBenefits && (
                <div className="text-center pt-2 pb-1">
                  <Button
                    variant="outline-primary"
                    onClick={() =>
                      setVisibleBenefitsCount((prev) => prev + BENEFITS_LOAD_STEP)
                    }
                  >
                    Cargar más beneficios
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

       {/* RANKING GLOBAL ABAJO */}
<div id="ranking-global-home" className="mt-5 mb-4">
  <GlobalRankingsHome />
</div>

      </div>
    </>
  );
};

export default HomeBeneficios;







